import { useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleAuth } from '@questlabs/auth-js';
import Header from './Header';
import SourcePanel from './SourcePanel';
import GeneratorPanel from './GeneratorPanel';
import Timeline from './Timeline';
import { extractFrames } from '../services/frameExtractor';
import { generateCues } from '../services/cues';
import { saveProject } from '../services/projects';

const PROJECT_ID = crypto.randomUUID();

export default function Studio({ user }) {
  const { sessionToken } = useGoogleAuth();
  const videoRef = useRef(null);
  const importRef = useRef(null);
  const [source, setSource] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [script, setScript] = useState('');
  const [interval, setInterval] = useState(1.5);
  const [maxFrames, setMaxFrames] = useState(36);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [cues, setCues] = useState([]);
  const [status, setStatus] = useState('Idle');
  const [error, setError] = useState('');

  const playableUrl = useMemo(() => source ? URL.createObjectURL(source) : videoUrl, [source, videoUrl]);
  const estimatedFrames = duration ? Math.min(maxFrames, Math.ceil(duration / interval)) : maxFrames;
  useEffect(() => () => {
    if (source && playableUrl) URL.revokeObjectURL(playableUrl);
  }, [source, playableUrl]);

  const project = () => ({
    id: PROJECT_ID,
    title: source?.name || 'Untitled Scene',
    screenplay: script,
    videoUrl,
    cues,
    settings: { interval, maxFrames }
  });

  const runGeneration = async () => {
    setError('');
    if (!videoRef.current || !playableUrl) return setError('Add a video file or public URL first.');
    if (!script.trim()) return setError('Add screenplay text before generating cues.');
    if (!sessionToken) return setError('Your secure session is not ready. Please sign in again.');

    try {
      setStatus('Preparing video');
      await videoRef.current.play().then(() => videoRef.current.pause()).catch(() => {});
      setStatus('Extracting frames');
      const frames = await extractFrames(videoRef.current, interval, maxFrames);
      setStatus('Sending to GlobalGPT');
      const nextCues = await generateCues({ screenplay: script, frames, sessionToken });
      setStatus('Validating cues');
      setCues(nextCues);
      await saveProject(sessionToken, { ...project(), cues: nextCues });
      setStatus('Complete');
    } catch (cause) {
      setStatus('Idle');
      setError(cause.message || 'The analysis could not be completed.');
    }
  };

  const exportProject = () => {
    const blob = new Blob([JSON.stringify(project(), null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'sceneflow-project.json';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const importProject = async (event) => {
    try {
      const data = JSON.parse(await event.target.files[0].text());
      setScript(data.screenplay || '');
      setVideoUrl(data.videoUrl || '');
      setCues(Array.isArray(data.cues) ? data.cues : []);
      setInterval(data.settings?.interval || 1.5);
      setMaxFrames(data.settings?.maxFrames || 36);
    } catch {
      setError('That project file is not valid SceneFlow JSON.');
    }
  };

  return (
    <div className="studio-shell">
      <Header user={user} onExport={exportProject} onImport={() => importRef.current?.click()} />
      <input ref={importRef} hidden type="file" accept=".json" onChange={importProject} />
      <main className="studio-main">
        <section className="workspace-intro">
          <p className="eyebrow">AI VIDEO REVIEW WORKSPACE</p>
          <h1>From screenplay<br />to synchronized cut.</h1>
          <p>Review generated footage against the written scene and create defensible timeline cues.</p>
        </section>
        <div className="workspace-grid">
          <SourcePanel {...{ source, setSource, videoUrl, setVideoUrl, script, setScript }} />
          <div className="review-column">
            <div className="player-frame">
              {playableUrl ? (
                <video
                  ref={videoRef}
                  src={playableUrl}
                  controls
                  crossOrigin="anonymous"
                  onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                  onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                />
              ) : <div className="player-empty"><span>16:9</span><p>Your footage will appear here</p></div>}
            </div>
            <GeneratorPanel
              {...{ interval, setInterval, maxFrames, setMaxFrames, estimatedFrames, status, error }}
              disabled={!playableUrl || !script.trim() || !['Idle', 'Complete'].includes(status)}
              onGenerate={runGeneration}
            />
          </div>
        </div>
        <Timeline cues={cues} setCues={setCues} currentTime={currentTime} onSeek={(time) => {
          if (videoRef.current) videoRef.current.currentTime = time;
        }} />
      </main>
    </div>
  );
}